import icon_keypad_lock from '../assets/icon_keypad_locked.svg';
import icon_keypad_unlock from '../assets/icon_keypad_unlocked.svg';
import icon_alert from '../assets/icon_alert.svg';
import icon_fan from '../assets/icon_fan.svg';
import icon_heating from '../assets/icon_heating.svg';
import icon_cooling from '../assets/icon_cooling.svg';
import icon_idle from '../assets/icon_idle.svg';
import icon_eco from '../assets/icon_eco.svg';
import icon_off from '../assets/icon_state_off.svg';
import icon_connectivity_problem from '../assets/icon_connectivity_problem.svg';

import { HVAC_MODE, THERMAL_ZONE_STATUS } from '../config/status';

export const getHvacMode = (zoneRealtimeData) => {
  if (!zoneRealtimeData) return null;
  const { heatBlocked, coolBlocked } = zoneRealtimeData;
  if (heatBlocked && coolBlocked) {
    return HVAC_MODE.FAN_ONLY;
  } else if (coolBlocked) {
    return HVAC_MODE.HEAT_ONLY;
  } else if (heatBlocked) {
    return HVAC_MODE.COOL_ONLY;
  } else {
    return HVAC_MODE.AUTO;
  }
};

export const setThermalZoneStatus = (zoneRealtimeData) => {
  if (!zoneRealtimeData) return null;
  const isOutOfRangeLow = zoneRealtimeData.outOfRangeLow || zoneRealtimeData.veryOutOfRangeLow;
  const isOutOfRangeHigh = zoneRealtimeData.outOfRangeHigh || zoneRealtimeData.veryOutOfRangeHigh;
  const inOverride = zoneRealtimeData.inOverride || zoneRealtimeData.inLocalOverride;

  if (inOverride) return THERMAL_ZONE_STATUS.IN_OVERRIDE;
  if (zoneRealtimeData.offline) return THERMAL_ZONE_STATUS.OFFLINE;
  if (zoneRealtimeData.unmanaged) return THERMAL_ZONE_STATUS.UNMANAGED;
  if (isOutOfRangeLow && zoneRealtimeData.heatBlocked) return THERMAL_ZONE_STATUS.HEAT_BLOCKED;
  if (isOutOfRangeHigh && zoneRealtimeData.coolBlocked) return THERMAL_ZONE_STATUS.COOL_BLOCKED;
  if (zoneRealtimeData.veryOutOfRangeLow || zoneRealtimeData.veryOutOfRangeHigh)
    return THERMAL_ZONE_STATUS.VERY_OUT_OF_RANGE;
  if (isOutOfRangeLow || isOutOfRangeHigh) return THERMAL_ZONE_STATUS.OUT_OF_RANGE;
  return THERMAL_ZONE_STATUS.IN_RANGE;
};

/*** function is meant to be an independent processor for the zone realtime data***/
const processRealtimeData = (zoneRealtimeData) => {
  // default values
  const returnRealtimeData = {
    currentTemperature: 'N/A',
    temperatureLowerBound: null,
    temperatureUpperBound: null,
    temperatureSetting: null,
    activity: (
      <div>
        <img src={icon_alert} alt="alert icon" width="15" /> None
      </div>
    ),
    thermostatIndicator: null,
    zoneStatus: null,
    zoneStatusCSS: '',
    thermostatMode: null,
    temperatureClass: '',
    scheduleOrOverride: null,
    keypadLock: (
      <div className="keypad-status zone-stat">
        <img src={icon_keypad_lock} alt="locked padlock icon" width="12" height="12" />
        <span>Locked</span>
      </div>
    ),
    controlState: null,
  };

  if (zoneRealtimeData) {
    // console.log('zoneRealtimeData: ', zoneRealtimeData);

    const currentTemperature = zoneRealtimeData.temperature;
    const lowerBound = zoneRealtimeData.temperatureLowerBound;
    const upperBound = zoneRealtimeData.temperatureUpperBound;
    const hvacMode = getHvacMode(zoneRealtimeData);
    const inOverride = zoneRealtimeData.inOverride || zoneRealtimeData.inLocalOverride;

    // save for later if needed
    // const isBelowTemperatureLowerBound =
    //   currentTemperature !== undefined &&
    //   lowerBound !== undefined &&
    //   currentTemperature < lowerBound;
    // const isAboveTemperatureUpperBound =
    //   currentTemperature !== undefined &&
    //   upperBound !== undefined &&
    //   currentTemperature > upperBound;

    if (zoneRealtimeData.temperature) {
      returnRealtimeData.currentTemperature = Math.round(currentTemperature);
      returnRealtimeData.temperatureLowerBound = Math.round(lowerBound);
      returnRealtimeData.temperatureUpperBound = Math.round(upperBound);
    }
    if (hvacMode) {
      returnRealtimeData.temperatureSetting = inOverride
        ? THERMAL_ZONE_STATUS.IN_OVERRIDE.label
        : hvacMode !== HVAC_MODE.AUTO
          ? hvacMode.label
          : null;
    }

    const thermalZoneStatus = setThermalZoneStatus(zoneRealtimeData);

    returnRealtimeData.zoneStatus = thermalZoneStatus.label;
    returnRealtimeData.zoneStatusCSS = thermalZoneStatus.cssClass;

    returnRealtimeData['keypadLock'] = (
      <div className="keypad-status">
        <img src={icon_keypad_unlock} alt="unlocked padlock icon" width="18" height="18" />
      </div>
    );
    if (zoneRealtimeData['localOverrideLocked']) {
      returnRealtimeData['keypadLock'] = (
        <div className="keypad-status">
          <img src={icon_keypad_lock} alt="locked padlock icon" width="18" height="18" />
        </div>
      );
    }

    if (zoneRealtimeData.activityState !== undefined) {
      switch (zoneRealtimeData.activityState.toLowerCase()) {
        case 'off':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_off} alt="off icon" width="18" height="18" />
            </div>
          );
          break;
        case 'heating':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_heating} alt="heating icon" width="18" height="18" />
            </div>
          );
          break;
        case 'cooling':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_cooling} alt="snowflake icon" width="18" height="18" />
            </div>
          );
          break;
        case 'fan':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_fan} alt="fan icon" width="18" height="18" />
            </div>
          );
          break;
        case 'idle':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_idle} alt="fan icon" width="18" height="18" />
            </div>
          );
          break;
        case 'economizing':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_eco} alt="leaf eco icon" width="24" height="24" />
            </div>
          );
          break;
        case 'problem':
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_alert} alt="alert icon" width="18" height="18" />
            </div>
          );
          break;
        default:
          returnRealtimeData.activityState = (
            <div className="activity-state">
              <img src={icon_off} alt="off icon" width="18" height="18" />
            </div>
          );
          break;
      }
    }

    if (zoneRealtimeData.unmanaged) {
      returnRealtimeData.controlState = 'Unmanaged';
    } else if (zoneRealtimeData.hasControlProblem) {
      returnRealtimeData.controlState = 'Control Problem';
    } else if (zoneRealtimeData.offline) {
      returnRealtimeData.controlState = 'Connectivity Problem';
    } else if (zoneRealtimeData.elexityOptimized) {
      returnRealtimeData.controlState = 'Elexity Optimized';
    } else if (zoneRealtimeData.inOverride || zoneRealtimeData.inLocalOverride) {
      returnRealtimeData.controlState = THERMAL_ZONE_STATUS.IN_OVERRIDE.label;
    }

    if (zoneRealtimeData.inOverride) {
      returnRealtimeData.scheduleOrOverride = `In Remote Override Until: ${zoneRealtimeData.overrideEnds}`;
    } else if (zoneRealtimeData.inLocalOverride) {
      returnRealtimeData.scheduleOrOverride = `In Local Override Until: ${zoneRealtimeData.localOverrideEnds}`;
    } else if (zoneRealtimeData.inEvent) {
      returnRealtimeData.scheduleOrOverride = `${zoneRealtimeData.eventName}`;
    } else if (zoneRealtimeData.inSchedule) {
      returnRealtimeData.scheduleOrOverride = `${zoneRealtimeData.scheduleName}`;
    } else if (zoneRealtimeData.unmanaged) {
      returnRealtimeData.scheduleOrOverride = 'Unmanaged';
    } else {
      returnRealtimeData.scheduleOrOverride =
        zoneRealtimeData.scheduleName !== null ? `${zoneRealtimeData.scheduleName}` : 'No Schedule';
    }
  } else {
    returnRealtimeData['activity'] = false;
  }

  return returnRealtimeData;
};

export default processRealtimeData;
